import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { ThunkServices } from '../store';
import { UserSelectionState, YesNoOptions } from '../user-selection/userSelection';
import { SHA1 } from 'crypto-js';
import { push } from 'connected-react-router';
import { appRoutesByName } from '../../containers/app/Routes';
import { TIMEZONE } from '../../utils/constants';
import { PayerDetails } from '../../components/customer-details-form/CustomerDetailsForm';
import moment from 'moment-timezone';

// Interfaces
interface RealexIframeResponse {
    resultCode: string;
    message: string;
}
export interface RealexPaymentSuccessResponse extends RealexIframeResponse {
    responseObj: { [key: string]: string };
}
export interface RealexPaymentErrorResponse extends RealexIframeResponse {
    responseObj?: { [key: string]: string };
}
export interface PaymentState {
    paymentResponse?: RealexPaymentSuccessResponse;
    paymentRequest?: endpoints.Payment;
    paymentError?: RealexPaymentErrorResponse;
    paymentRequestLoading: boolean;
}
export interface CardholderDetails {
    emailAddress: string;
    phoneNumber: string;
    billingAddress1: string;
    billingAddress2: string;
    billingAddress3?: number;
    billingCity: string;
    billingEircode?: string;
}

// Initial State
export const initialPaymentState: PaymentState = {
    paymentResponse: undefined,
    paymentRequest: undefined,
    paymentRequestLoading: false,
    paymentError: undefined,
};

// Thunks
export const getPayment = createAsyncThunk<
    endpoints.Payment, // Return type of the thunk
    void, // Argument type (none in this case)
    { state: RootState; extra: ThunkServices } // ThunkAPI configuration
>('payment/getPayment', async (_, { dispatch, getState, extra: { bookingService } }) => {
    dispatch(setPaymentRequestLoading( {paymentRequestLoading: true} ));
    const { userSelection } = getState();
    const hash = generateHash(userSelection);
    if (hash && userSelection.productId) {
        try {
            const response = await bookingService.getPayment(
                userSelection.productId,
                hash.toString(),
                mapCardHolderDetails(userSelection)
            );
            dispatch(setPaymentRequest({ paymentRequest: response.data }));
            dispatch(setPaymentRequestLoading( {paymentRequestLoading: false} ));
            return response.data;
        } catch (error) {
            dispatch(setPaymentRequestLoading( {paymentRequestLoading: false} ));
            dispatch<any>(push(appRoutesByName.errorPage.path));
            throw error;
        }
    }
    dispatch<any>(push(appRoutesByName.errorPage.path));
    throw new Error('Unable to send booking hash');
});

// Slice
export const paymentSlice = createSlice({
    name: 'payment',
    initialState: initialPaymentState,
    reducers: {
        setPaymentResponse: (state, action: PayloadAction<{ paymentResponse: RealexPaymentSuccessResponse }>) => {
            state.paymentResponse = action.payload.paymentResponse;
        },
        setPaymentRequest: (state, action: PayloadAction<{ paymentRequest: object }>) => {
            state.paymentRequest = action.payload.paymentRequest as endpoints.Payment;
        },
        setPaymentError: (state, action: PayloadAction<{ paymentError: RealexPaymentErrorResponse }>) => {
            state.paymentError = action.payload.paymentError;
        },
        setPaymentRequestLoading: (state, action: PayloadAction<{paymentRequestLoading :boolean}> ) => {
            state.paymentRequestLoading = action.payload.paymentRequestLoading;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPayment.pending, (state) => {
                state.paymentRequestLoading = true;
            })
            .addCase(getPayment.fulfilled, (state) => {
                state.paymentRequestLoading = false;
            })
            .addCase(getPayment.rejected, (state) => {
                state.paymentRequestLoading = false;
            });
    },
});

// Actions
export const {
    setPaymentResponse,
    setPaymentRequest,
    setPaymentError,
    setPaymentRequestLoading,
} = paymentSlice.actions;

// Reducer
export const paymentReducer = paymentSlice.reducer;

// Helper Functions
export function generateHash(userSelection: UserSelectionState) {
    const { productId, lessonTime, location, pupilDetails } = userSelection;
    const geoareaId = location && location.geoareaId;
    if (!pupilDetails.purchaser || !lessonTime) {
        return undefined;
    }
    const { pupil, thirdParty } = pupilDetails;
    const { firstName, surname, dob, addressLine1, town, email } = pupil;
    const pupilKeyDetails =
        '' +
        moment(lessonTime).toISOString() +
        geoareaId +
        productId +
        addressLine1.trim() +
        town.trim() +
        email.trim() +
        firstName.trim() +
        surname.trim() +
        moment(`${dob.dobYear}-${dob.dobMonth}-${dob.dobDay}`)
            .tz(TIMEZONE)
            .format('YYYY-MM-DD');

    if (thirdParty) {
        let thirdPartyKeyDetails = thirdParty.email.trim();
        if (thirdParty.firstName !== undefined && thirdParty.surname !== undefined) {
            thirdPartyKeyDetails = thirdPartyKeyDetails + thirdParty.firstName.trim() + thirdParty.surname.trim();
        }
        return SHA1(pupilKeyDetails + thirdPartyKeyDetails);
    } else {
        return SHA1(pupilKeyDetails);
    }
}

function mapCardHolderDetails(userSelection: UserSelectionState): CardholderDetails {
    const payer: PayerDetails =
        userSelection.pupilDetails.sameDetails === YesNoOptions.Yes
            ? userSelection.pupilDetails.pupil
            : userSelection.pupilDetails.thirdParty;

    return {
        emailAddress: payer.email,
        phoneNumber: payer.mobile,
        billingAddress1: payer.addressLine1,
        billingAddress2: payer.addressLine2,
        billingAddress3: payer.countyId,
        billingCity: payer.town,
        billingEircode: payer.eircode,
    };
}